import { useEffect, useCallback, useRef } from "react";
import { useWorkerCreation } from "@/context/WorkerCreationContext";
import {
  generateRandomWorkerName,
  generateJobTitleFromName,
} from "@/utils/nameGenerator";

/**
 * Custom hook for managing worker name generation
 * Handles the logic of when to generate random names vs using template names
 */
export function useNameGenerator() {
  const { workerData, updateWorkerData, selectedTemplate, resetWorkerData } =
    useWorkerCreation();
  
  // Track if job title has been manually edited by user
  const jobTitleManuallyEdited = useRef(false);
  const lastGeneratedJobTitle = useRef<string>("");

  /**
   * Generates a new random name and job title and updates the worker data
   */
  const generateNewName = useCallback(() => {
    const newName = generateRandomWorkerName();
    const newJobTitle = generateJobTitleFromName(newName);
    lastGeneratedJobTitle.current = newJobTitle;
    updateWorkerData({ name: newName, jobTitle: newJobTitle });
    jobTitleManuallyEdited.current = false;
  }, [updateWorkerData]);

  /**
   * Resets the worker data and generates a new random name
   */
  const resetAndGenerateName = useCallback(() => {
    resetWorkerData();
    // Generate new name after reset
    setTimeout(() => {
      generateNewName();
    }, 0);
  }, [resetWorkerData, generateNewName]);

  /**
   * Handles template selection and name updates
   * - If template is selected, use template's name and job title
   * - If template is cleared, keep the current name (don't regenerate)
   * - If no template and name is default "Worker", generate random name and job title
   */
  useEffect(() => {
    // Only apply template name/job-title if we are creating a new worker (no id yet)
    if (selectedTemplate && !workerData._id) {
      updateWorkerData({
        name: selectedTemplate.name,
        jobTitle: selectedTemplate.default_job_title || "",
      });
      jobTitleManuallyEdited.current = false;
    } else if (
      !selectedTemplate &&
      (workerData.name === "Worker" || !workerData.name)
    ) {
      // No template and still using default name - generate random name and job title
      generateNewName();
    }
    // If template is cleared, we keep the current name (don't regenerate)
    // If in edit mode (workerData._id exists), we don't overwrite the name.
  }, [selectedTemplate, workerData._id, updateWorkerData, generateNewName]);

  /**
   * Checks if the current name is a generated random name
   * (not a template name or user-customized name)
   */
  const isGeneratedName = useCallback(() => {
    if (selectedTemplate && workerData.name === selectedTemplate.name) {
      return false; // Template name
    }

    // Check if name matches the pattern "Adjective Personality"
    const nameParts = workerData.name.split(" ");
    if (nameParts.length === 2) {
      // Could be a generated name, but we can't be 100% sure
      // since user might have typed something that matches the pattern
      return true;
    }

    return false;
  }, [workerData.name, selectedTemplate]);

  /**
   * Updates the job title based on the current name
   * Only auto-generates if the job title hasn't been manually edited
   */
  const updateJobTitleFromName = useCallback(() => {
    // Only auto-generate if:
    // 1. No template is selected
    // 2. Name is not the default "Worker"
    // 3. Job title hasn't been manually edited by user
    if (!selectedTemplate && workerData.name !== "Worker" && !jobTitleManuallyEdited.current) {
      const newJobTitle = generateJobTitleFromName(workerData.name);
      lastGeneratedJobTitle.current = newJobTitle;
      updateWorkerData({ jobTitle: newJobTitle });
    }
  }, [workerData.name, selectedTemplate, updateWorkerData]);

  /**
   * Marks the job title as manually edited by the user
   */
  const markJobTitleAsManuallyEdited = useCallback(() => {
    jobTitleManuallyEdited.current = true;
  }, []);

  /**
   * Resets the manual edit flag when appropriate
   */
  const resetJobTitleEditFlag = useCallback(() => {
    jobTitleManuallyEdited.current = false;
  }, []);

  return {
    currentName: workerData.name,
    generateNewName,
    resetAndGenerateName,
    updateJobTitleFromName,
    markJobTitleAsManuallyEdited,
    resetJobTitleEditFlag,
    isGeneratedName,
    hasTemplate: !!selectedTemplate,
  };
}
